import React from "react";
import {
  DemoResponsiveGrid,
  DemoWorkbenchCard,
  DemoWorkbenchCopy,
  DemoWorkbenchFact,
  DemoWorkbenchFactGrid,
  DemoWorkbenchInset,
  DemoWorkbenchPill,
  DemoWorkbenchStack,
  DemoWorkbenchTable,
  type DemoWorkbenchFactTone,
} from "@csk/demo-ui";
import mlGovernanceLayerData from "../data/ml-governance-layers.json";

type LayerType = "linear" | "attention" | "recurrent" | "normalization";

interface LayerMetrics {
  readonly name: string;
  readonly conditionNumber: number;
  readonly spectralGap: number;
  readonly stabilityMargin: number;
  readonly detMagnitude: number;
}

interface LayerTypeEntry {
  readonly id: LayerType;
  readonly generator: string;
  readonly axis: string;
  readonly colorClass: string;
  readonly backgroundClass: string;
  readonly borderClass: string;
  readonly description: string;
  readonly watch: string;
  readonly risk: string;
  readonly layer: LayerMetrics;
}

const ML_LAYER_TYPES = mlGovernanceLayerData.layerTypes as readonly LayerTypeEntry[];

interface LayerMetricFact {
  readonly label: string;
  readonly value: string | number;
  readonly tone: DemoWorkbenchFactTone;
}

function layerMetricFacts(
  type: LayerType,
  layer: LayerMetrics,
): readonly LayerMetricFact[] {
  if (type === "linear") {
    return [
      {
        label: "κ(W)",
        value: layer.conditionNumber,
        tone: layer.conditionNumber > 10 ? "warning" : "success",
      },
      { label: "|det|", value: layer.detMagnitude, tone: "success" },
    ];
  }
  if (type === "attention") {
    return [
      {
        label: "gap",
        value: layer.spectralGap,
        tone: layer.spectralGap < 0.1 ? "danger" : "success",
      },
      { label: "κ", value: layer.conditionNumber, tone: "success" },
    ];
  }
  if (type === "recurrent") {
    return [
      {
        label: "margin",
        value: layer.stabilityMargin,
        tone: layer.stabilityMargin < 0.1 ? "danger" : "success",
      },
      { label: "|det|", value: layer.detMagnitude, tone: "success" },
    ];
  }
  return [
    { label: "tr(Γ)", value: layer.detMagnitude.toFixed(1), tone: "success" },
    { label: "κ", value: layer.conditionNumber, tone: "success" },
  ];
}

const MLGovernancePanel = (): React.JSX.Element => (
  <DemoWorkbenchStack>
    <DemoWorkbenchCard title="ML Layer → Governance Axis (§13.3.2)">
      <DemoWorkbenchCopy>
        Each ML layer type is the computational manifestation of the governance
        generator it maps to.
      </DemoWorkbenchCopy>
    </DemoWorkbenchCard>

    <DemoResponsiveGrid minColumnWidth={280} gap={12}>
      {ML_LAYER_TYPES.map((entry) => {
        const layer = entry.layer;
        return (
          <DemoWorkbenchInset
            key={entry.id}
            className={`border ${entry.backgroundClass} ${entry.borderClass}`}
            title={
              <span className={`${entry.colorClass} font-bold text-sm`}>
                {entry.generator}: {entry.axis}
              </span>
            }
            right={<DemoWorkbenchPill>{entry.id}</DemoWorkbenchPill>}
          >
            <DemoWorkbenchCopy size="xs" tone="body" className="mb-1">
              {entry.description}
            </DemoWorkbenchCopy>
            <DemoWorkbenchCopy size="xs" tone="muted" className="mb-1">
              Watch: {entry.watch}
            </DemoWorkbenchCopy>
            <DemoWorkbenchCopy size="xs" tone="danger" className="mb-2">
              Risk: {entry.risk}
            </DemoWorkbenchCopy>
            <DemoWorkbenchFactGrid>
              {layerMetricFacts(entry.id, layer).map((fact) => (
                <DemoWorkbenchFact
                  key={fact.label}
                  label={fact.label}
                  value={fact.value}
                  tone={fact.tone}
                  mono
                />
              ))}
            </DemoWorkbenchFactGrid>
          </DemoWorkbenchInset>
        );
      })}
    </DemoResponsiveGrid>

    <DemoWorkbenchInset title="Model Layer Inventory">
      <DemoWorkbenchTable
        compact
        headers={["Generator", "Layer", "Type", "Axis"]}
        rows={ML_LAYER_TYPES.map((entry) => {
          const layer = entry.layer;
          return [
            <span key={`${layer.name}-generator`} className={entry.colorClass}>
              {entry.generator}
            </span>,
            layer.name,
            entry.id,
            <span key={`${layer.name}-axis`} className={entry.colorClass}>
              {entry.axis}
            </span>,
          ];
        })}
      />
    </DemoWorkbenchInset>

    <DemoWorkbenchInset title="MIT Verification" className="text-sm">
      <DemoWorkbenchFactGrid minColumnWidth={120}>
        <DemoWorkbenchFact
          label="Authority closed"
          value="C1 ✓"
          tone="success"
        />
        <DemoWorkbenchFact label="Gap = 0.15" value="C2 ⚠" tone="warning" />
        <DemoWorkbenchFact label="Stable (0.8)" value="C3 ✓" tone="success" />
        <DemoWorkbenchFact label="det ≠ 0" value="C4 ✓" tone="success" />
      </DemoWorkbenchFactGrid>
    </DemoWorkbenchInset>
  </DemoWorkbenchStack>
);

export default MLGovernancePanel;
